import { Component, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormattedContacts, PoEmailData } from '../../../../models/poData/FormattedContacts';
import {EmailStatusChangeData} from '../../../../models/email/EmailStatusChangeData.interface';
import { hasContactNeedingEmail } from '../../../../domain/receiving-log/rules/contacts.rules';
import { ContactsService } from '../../../../service/contact.service';
import { EmployeeDirectoryError } from '../../../../models/contacts/EmployeeDirectoryError';
import { COMPANY_EMAIL_DOMAIN, TypedEmail, typedEmailToAdd } from '../../../../domain/receiving-log/rules/employee-directory.rules';

/**
 * AddContactComponent
 * 
 * This component is responsible for managing contact selection for a log entry, 
 * with options provided from PO contact details.
 * It allows users to select a primary contact, add or remove contacts from the notify list,
 * and filter available contacts from employee emails.
 * 
 * This component is used within a modal on the LogFormComponent that enables users to edit and manage contacts 
 * related to a PO. It interacts with the parent component via event emitters to update the 
 * primary contact and notify list.
 */

@Component({
  selector: 'app-add-contact',
  templateUrl: './add-contact.component.html',
  styleUrls: ['./add-contact.component.css'],
})
export class AddContactComponent implements OnInit, OnDestroy {
  @Input() primaryContact: string = ''; // Stores the selected primary contact email.
  @Input() allContactInfo: FormattedContacts= new FormattedContacts(); // Contains all contact details for the PO.
  @Input() notifyList: PoEmailData[] = []; // List of emails to be notified.
  @Input() employeeEmails: string[] = []; // List of all available employee emails.

  @Output() updateNotifyList = new EventEmitter<PoEmailData[]>(); // Emits updated notify list when changes occur.
  @Output() updatePrimaryContact = new EventEmitter<string>(); // Emits primary contact updates.

  public searchQuery = ''; // Stores the user’s search input for filtering contacts.
  public filteredContacts: string[] = []; // Stores the filtered contacts matching the search query.
  public allContactEmails: PoEmailData[] = []; // Stores all available contact emails.
  public emailStatusMap: Record<string, boolean> = {}; // Tracks which emails are in the notify list (true = in list, false = not in list).

  /** Mirrors ContactsService.isLoadingEmails, so this picker can show a loading state. */
  public employeesLoading = false;
  /** Mirrors ContactsService.emailsError, so a failed fetch explains itself here too. */
  public employeesError: EmployeeDirectoryError | null = null;

  private _loadingSub: Subscription | null = null;
  private _errorSub: Subscription | null = null;

  constructor(private _contactsService: ContactsService) {
   }

  ngOnInit(): void {
    this._loadingSub = this._contactsService.isLoadingEmails.subscribe(loading => {
      this.employeesLoading = loading;
    });
    this._errorSub = this._contactsService.emailsError.subscribe(error => {
      this.employeesError = error;
    });
  }

  ngOnDestroy(): void {
    this._loadingSub?.unsubscribe();
    this._errorSub?.unsubscribe();
  }

  /**
   * True when this PO named a contact with no usable address, which is what the GP
   * warning is really about — a GP PO whose contacts all arrived as real addresses
   * has nothing to caveat.
   */
  public get hasContactNeedingEmail(): boolean {
    return hasContactNeedingEmail(this.allContactInfo);
  }


  /**
   * Lifecycle hook that runs when input properties change.
   * Updates local properties when notifyList, primaryContact,
   * allContactInfo, or employeeEmails change.
   * Also rebuilds the emailStatusMap when allContactInfo changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Update local notifyList when notifyList input property changes
    if ('notifyList' in changes) {
      const notifyList = changes['notifyList'].currentValue;
      this.notifyList = notifyList;
    }
    // Update local primaryContact when primaryContact input property changes
    if ('primaryContact' in changes) {
      const primaryContact = changes['primaryContact'].currentValue;
      this.primaryContact = primaryContact;
    }
  

      // Update local employeeEmails when employeeEmails input property changes
    if('employeeEmails' in changes) {
      const employeeEmails = changes['employeeEmails'].currentValue;
      this.employeeEmails = employeeEmails;
    }
    
    // Update local allContactInfo and allContactEmails and rebuild/update the emailStatusMap 
    // when ahen allContactInfo input property changes
    if ('allContactInfo' in changes) {
      let allContactInfo= changes['allContactInfo'].currentValue;
      if (allContactInfo === undefined) {
       allContactInfo = new FormattedContacts();
      }
      this.extractAllContactInfo(allContactInfo);
  }
  }

  public extractAllContactInfo(allContactInfo: FormattedContacts): void {

    const allContactEmails = allContactInfo.allContactEmails;
    
    this.allContactInfo = allContactInfo;
    this.allContactEmails = allContactEmails;

// Build a map (emailStatusMap) to track which emails are in the notify list.
// Each email in allContactEmails is checked against notifyList.
// If the email exists in notifyList, it's marked as true (selected for notification); otherwise, it's false.
   const emailStatusMap: Record<string, boolean> = {};
   for (const email of allContactEmails) {
    emailStatusMap[email.contact] = this.notifyList.some((item: PoEmailData) => item.contact === email.contact);
}

this.emailStatusMap = emailStatusMap;
  }

  /**
   * Toggles the selected primary contact.
   * If the clicked contact is already the primary, it is deselected.
   * Otherwise, it is set as the new primary contact.
   * Emits the updated primary contact to the parent component.
   */
public handleUpdatePrimaryContact(contact: string) {
  let primaryContact;
  if (this.primaryContact===contact) {
    primaryContact = '';
    }
    else {
    primaryContact = contact;
    }
    this.primaryContact = primaryContact;
  this.updatePrimaryContact.emit(primaryContact);
}


  /**
   * Adds an email to the notify list if it is not already included.
   * Updates the emailStatusMap and clears the search query and filtered contacts.
   * Emits the updated notify list to the parent component.
   */
    public addToNotifyList(email: string) {
      const emailData = { contact: email, isValidEmail: true }; 
    // Check if the email already exists in the notifyList by comparing the contact field
    if (!this.notifyList.some((item: PoEmailData) => item.contact === email)) {
      const newNotifyList = [emailData, ...this.notifyList];
      this.notifyList = newNotifyList;
      this.emailStatusMap[email] = true; // Mark email as added
      this.handleNotifyListChange(newNotifyList);
  }
     // Clear search and filtered contacts
      this.searchQuery = '';
      this.filteredContacts = [];
    }


  /**
   * Removes an email from the notify list.
   * Updates the emailStatusMap and emits the updated notify list to the parent component.
   */
 public  removeFromNotifyList(email:string) {
    // Filter the notifyList to remove the emailData object with the matching contact
    const newNotifyList = this.notifyList.filter((e: PoEmailData) => e.contact !== email);

    // Remove the email from the emailStatusMap and mark it as false
    this.emailStatusMap[email] = false;

    // Update the notifyList
    this.notifyList = newNotifyList;
    this.handleNotifyListChange(newNotifyList);
  }


 /**
   * Handles email checkbox changes from PO-provided contacts.
   * Calls addToNotifyList or removeFromNotifyList based on checkbox status.
   */
 public  updateNotifyListFromPO(event: EmailStatusChangeData) {
    if (event.status) {
      this.addToNotifyList(event.contact);
    } else {
      this.removeFromNotifyList(event.contact);
    }
  }

  /**
   * Emits the updated notify list to the parent component.
   */
  public handleNotifyListChange(notifyList: PoEmailData[]): void {
    this.updateNotifyList.emit(notifyList);
  }

 /**
   * Filters the list of available employee emails based on the search query.
   * Updates the filteredContacts array to display matching search results.
   */
 public  filterContacts() {
    // Match every token rather than the raw string, so a PO name ("Jane Doe")
    // finds the directory address that spells it differently
    // (jane.doe@macmiller.com). A single-word query still behaves as a substring.
    const tokens = this.searchQuery.toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);
    if (!tokens.length) { this.filteredContacts = []; return; }

    this.filteredContacts = this.employeeEmails.filter(email => {
      const candidate = email.toLowerCase();
      return tokens.every(token => candidate.includes(token));
    });
  }

  /**
   * Seeds the employee search with a PO contact's name, so the user can pick the
   * real person out of the directory. The PO gave the name but no address, and none
   * is constructed from it.
   */
  public onFindInDirectory(name: string): void {
    this.searchQuery = name;
    this.filterContacts();
  }

  /**
   * True once the directory has loaded, there's a query, and it matched no one
   * — a real answer, not the same blank space a failed or in-progress fetch
   * would otherwise leave.
   */
  public get noSearchMatches(): boolean {
    return !this.employeesLoading
      && !this.employeesError
      && this.employeeEmails.length > 0
      && !!this.searchQuery.trim()
      && this.filteredContacts.length === 0
      && !this.typedEmail;
  }

  public readonly companyDomain = COMPANY_EMAIL_DOMAIN;

  /** An address built from the typed text that the directory doesn't list, offered as its own row. See typedEmailToAdd. */
  public get typedEmail(): TypedEmail | null {
    return typedEmailToAdd(this.searchQuery, this.employeeEmails, this.notifyList.map(n => n.contact));
  }

  /** Retries the directory fetch after a failure. Goes through the service's one refetch path. */
  public retryEmployeeFetch(): void {
    this._contactsService.refreshCompanyEmails().subscribe({
      // Failure is already toasted and recorded on emailsError by the service.
      error: () => undefined,
    });
  }
}